// Entry point: wires settings, input, the fixed 100 Hz simulation, rendering and UI together.

import './render/legacyLook'; // first: turns colour management off before any colour is created
import './styles.css';

import { MapAudio } from './audio/mapAudio';
import { Sound } from './audio/sound';
import { Music, STATIONS, type Station } from './audio/music';
import { InputState } from './input/inputState';
import { inputName, type Action } from './input/keybinds';
import { PointerLock } from './input/pointerLock';
import { isTouchDevice, TouchControls } from './input/touchControls';
import { buildCourse } from './maps/procedural/course';
import { buildBoxLevel } from './maps/boxlevel/build';
import { BUILTIN_MAPS, builtinLevel } from './maps/boxlevel/builtin';
import { MapOpen } from './ui/mapOpen';
import type { LevelDef } from './maps/boxlevel/types';
import { BoxLevelView } from './render/boxLevelView';
import { LevelEditor } from './editor/editor';
import { newLevel } from './editor/editorModel';
import {
  deleteLevel,
  listLevels,
  loadLevel,
  loadLevelRecord,
  parseLevelFile,
  saveLevel,
  saveProof,
  setPublished,
} from './storage/levelStore';
import { online, OnlineError } from './online/client';
import {
  canonicalLevel,
  fromBase64Url,
  normalizeShareCode,
  levelFromCode,
  levelKey,
  levelToCode,
  packLevel,
  unpackLevel,
} from './online/levelCode';
import {
  decodeReplay,
  encodeReplayAsync,
  replayCmd,
  RunRecorder,
  rulesKey,
  rulesLabel,
  type Replay,
} from './online/replay';
import { ghostPath } from './online/ghost';
import { mapBoard } from './online/mapBoard';
import { ReplayBot } from './render/replayBot';
import { Account } from './ui/account';
import { PlayerMaps } from './ui/playerMaps';
import { Scoreboard } from './ui/scoreboard';
import { SettingsNav } from './ui/settingsNav';
import { CrosshairPanel } from './ui/crosshair';
import { BoardModal } from './ui/boardModal';
import { mapLink } from './ui/playerMaps';
import { CourseView } from './render/courseView';
import { FINISHES, randomFinish, type Finish } from './render/finishes';
import { KNIVES, type KnifeKind } from './render/knives';
import { levelTextureEvents } from './render/proceduralTextures';
import { creditFor } from './community/mapCredits';
import { CommunityTab } from './ui/community';
import { CreditsPanel } from './ui/credits';
import { MenuTabs } from './ui/menuTabs';
import { GameRenderer, type ViewAngles } from './render/gameRenderer';
import type { LevelView } from './render/levelView';
import type { MapView } from './render/mapView';
import { DT } from './sim/constants';
import { createCmd } from './sim/input';
import {
  EV_BROKE,
  EV_FINISHED,
  EV_HIDE_FINISH,
  EV_JUMPED,
  EV_KNIFE_HIT,
  EV_LANDED,
  EV_STAGE,
  EV_TELEPORTED,
  Simulation,
  type Level,
} from './sim/simulation';
import { HULL_MAXS_STAND, HULL_MINS, horizontalSpeed, type SpawnPoint } from './sim/player';
import { MASK_PLAYERSOLID } from './sim/collision/types';
import { Chat } from './ui/chat';
import { RadioMenu, type MenuItem } from './ui/radioMenu';
import {
  KNIFE_DEPLOY,
  SELECT_GIVE_KNIFE,
  SELECT_GIVE_USP,
  USP,
  WEV_DROPPED,
  WEV_FIRED,
  WEV_RELOAD,
  WEV_SILENCER,
  WEV_SLASH,
  WEV_STAB,
  WEV_SWITCHED,
  type WeaponId,
} from './sim/weapons';
import { DT as TICK_DT } from './sim/constants';
import {
  addMapExtras,
  deleteMap,
  deleteZones,
  getZones,
  listMaps,
  saveZones,
  loadMapFile,
  mapId,
  saveMap,
  touchMap,
  clearModelFiles,
  loadSkinFiles,
  type CachedMap,
} from './storage/mapCache';
import type { GameVpk } from './maps/bsp/workerProtocol';
import { GameFolder } from './storage/gameFolder';
import { getPB, getSplits, loadSettings, saveSettings, setPB, setSplits } from './storage/settings';
import { fetchStandardZones, STANDARD_ZONES_NAME } from './online/standardZones';
import { emptyZones, zoneFromCorners, zonesFromJson, zonesToJson, type MapZones } from './storage/zoneFile';
import type { Zone } from './sim/zones';
import { DebugOverlay } from './ui/debugOverlay';
import { Hud, fmtTime } from './ui/hud';
import { MainMenu } from './ui/mainMenu';
import { Menu, type SettingsChange } from './ui/menu';

const $ = (id: string) => document.getElementById(id)!;
const DEG = Math.PI / 180;
const MAX_PITCH = 89 * DEG;

// A tab opened before a new deploy asks for code chunks that no longer exist (their names change
// every build). Reload to get the new version, at most once a minute so a real outage can't loop.
window.addEventListener('vite:preloadError', (e) => {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem('bhop-reloaded-at') ?? 0);
  } catch {
    // storage blocked
  }
  if (Date.now() - last < 60_000) return;
  e.preventDefault();
  try {
    sessionStorage.setItem('bhop-reloaded-at', String(Date.now()));
  } catch {
    // storage blocked
  }
  location.reload();
});

const settings = loadSettings();

/** View angles in radians, Source convention (pitch positive = down). Owned by the client, like in Source. */
const view: ViewAngles = { yaw: 0, pitch: 0 };
const cmd = createCmd();

const gr = new GameRenderer($('view'), settings.lowLatency);
gr.showKnife = settings.knife;
gr.setFov(settings.fov);
gr.setMaxPixelRatio(settings.maxPixelRatio);
function applyViewmodelSettings() {
  gr.viewmodel.setFinish(settings.finish);
  gr.viewmodel.setFov(settings.viewmodelFov);
  gr.viewmodel.setLeftHanded(settings.leftHanded);
  gr.viewmodel.setUseStudio(settings.cssModels);
}
applyViewmodelSettings();
const sound = new Sound();
sound.setVolume(settings.volume / 100);
const music = new Music();
music.setVolume(settings.musicVolume / 100);
music.setMuted(settings.musicMuted);
music.setStation(settings.musicStation);
{
  const sel = $('sStation') as HTMLSelectElement;
  for (const st of STATIONS) sel.add(new Option(st.label, st.id));
  sel.value = settings.musicStation;
  sel.addEventListener('change', () => {
    settings.musicStation = sel.value as Station;
    saveSettings(settings);
    music.setStation(settings.musicStation);
  });
  $('npSkip').addEventListener('click', () => music.next());
}
gr.viewmodel.onRigLoaded = () => gr.prewarm();
// Image textures arrive a moment after a level starts: upload them before they're seen.
let warmTimer = 0;
levelTextureEvents.onArrive = () => {
  clearTimeout(warmTimer);
  warmTimer = window.setTimeout(() => gr.prewarm(), 50);
};
// Browsers only allow audio after a user gesture.
window.addEventListener(
  'pointerdown',
  () => {
    sound.unlock();
    // Browsers only start audio after a click: try the music again.
    if (musicWanted()) void music.setPlaying(true);
  },
  { capture: true },
);
window.addEventListener('keydown', () => sound.unlock(), { capture: true });

const hud = new Hud();
const chat = new Chat(document.body);
chat.setSkin(settings.finish);
chat.setShown(false);
const radio = new RadioMenu(document.body);
hud.setKeyLabels(settings.binds);
const input = new InputState();
input.setBinds(settings.binds);
const menu = new Menu(settings);
const mainMenu = new MainMenu();
const tabs = new MenuTabs();
const community = new CommunityTab();
community.onScores = (board, title) => mainMenu.onScores(board, title);
new CreditsPanel();
const scoreboard = new Scoreboard();
const playerMaps = new PlayerMaps();
const account = new Account();
account.onRecoveryKey = () => {
  openNameSettings();
  $('accMsg').textContent = 'Paste your recovery key below, then press Play again.';
};
const settingsNav = new SettingsNav();
new CrosshairPanel(settings.crosshair, () => saveSettings(settings));
/** Hiscore rules of the current settings. */
const currentRules = () => ({
  airaccel: settings.airaccel,
  autobhop: settings.autobhop,
  slopeSlide: settings.slopeSlide,
});
playerMaps.rules = () => ({ key: rulesKey(currentRules()), label: rulesLabel(currentRules()) });
/** Open Settings at the hiscore name. */
function openNameSettings() {
  mainMenu.show('home');
  tabs.show('settings');
  settingsNav.show('online');
}
const boardModal = new BoardModal();
mainMenu.onScores = (board, title) =>
  boardModal.open(board, title, { key: rulesKey(currentRules()), label: rulesLabel(currentRules()) });

tabs.onOpen = (tab) => {
  if (tab === 'community') community.start();
  if (tab === 'maps') playerMaps.start();
};
if (tabs.current === 'community') community.start();
if (tabs.current === 'maps') playerMaps.start();
const lock = new PointerLock(gr.canvas);
/** Phone or tablet: joysticks instead of keyboard and mouse, automatic jumping, landscape only. */
const touch = isTouchDevice();
lock.touchMode = touch;
const touchUi = new TouchControls(input);
touchUi.assist = settings.touchAssist;
touchUi.lookSpeed = settings.touchLook;
{
  const sl = $('sTouchLook') as HTMLInputElement;
  const out = $('sTouchLookVal');
  const show = () => (out.textContent = settings.touchLook.toFixed(2) + '×');
  sl.value = String(settings.touchLook);
  show();
  sl.addEventListener('input', () => {
    settings.touchLook = touchUi.lookSpeed = Number(sl.value);
    show();
    saveSettings(settings);
  });
}
{
  const cb = $('sAssist') as HTMLInputElement;
  cb.checked = settings.touchAssist;
  cb.addEventListener('change', () => {
    settings.touchAssist = touchUi.assist = cb.checked;
    saveSettings(settings);
  });
}
if (touch) {
  document.body.classList.add('touch');
  // Jumping is automatic on touch screens, which needs autobhop.
  settings.autobhop = true;
}
touchUi.onPause = () => lock.release();
touchUi.onRestart = () => restart();
let touchHelpShown = false;
/** Paused because the phone was turned upright: resume when it's sideways again. */
let pausedUpright = false;
const portrait = window.matchMedia('(orientation: portrait)');
/** Touch screens play in landscape only: turning upright pauses, turning back resumes. */
function checkOrientation() {
  if (!touch) return;
  if (lock.locked && portrait.matches) {
    pausedUpright = true;
    lock.release();
    $('lockMsg').textContent = 'Turn your phone sideways to keep playing.';
  } else if (pausedUpright && !portrait.matches && mainMenu.screen === 'paused') {
    pausedUpright = false;
    lock.request();
  }
}
portrait.addEventListener('change', checkOrientation);
window.addEventListener('resize', checkOrientation);
const debug = new DebugOverlay($('debug'), gr.renderer);
debug.setVisible(settings.debug);

/* ---------------- Levels ---------------- */

/** 'published': a builder map someone published (played by its code, with online hiscores). */
type LevelKind = 'course' | 'map' | 'builtin' | 'custom' | 'published';
let sim: Simulation;
/** The timer state last frame: leaving the start zone is noted for the debug overlay's spike lines. */
let lastTimerState = 'zone';
let levelKind: LevelKind = 'course';
let levelName = '';
/** The box level being played (built-in, own or published). */
let currentDef: LevelDef | null = null;
/** Share code of the published map being played. */
let publishedCode: string | null = null;
/** Every command since the last restart, so finished runs can be checked by the server. */
const recorder = new RunRecorder();
let pb: number | null = null;
/** The loaded map's view and timer zones (null on the built-in course). */
let mapView: MapView | null = null;
let mapAudio: MapAudio | null = null;
let zones: MapZones | null = null;
let turnDir = 0;
let turnDecay = 0;

/** The record run on this level (from the replay bot), for spectating. */
let botRecord: { replay: Replay; name: string; ticks: number } | null = null;
/**
 * Spectating the record: the game plays its recorded commands instead of yours, so the view,
 * knife, blocks and sounds are exactly the record holder's run, from leaving the start zone to the
 * end zone, over and over. `i` is the next tick; the angles are kept to smooth the view.
 */
let spectating: {
  rec: { replay: Replay; name: string; ticks: number };
  i: number;
  yaw0: number;
  pitch0: number;
  yaw1: number;
  pitch1: number;
} | null = null;

/** True while the builder itself loads a level (opening, rebuilding, play testing). */
let builderLoad = false;
/** Run a level load for the builder, so it doesn't close the builder. */
function asBuilder(load: () => void) {
  builderLoad = true;
  try {
    load();
  } finally {
    builderLoad = false;
  }
}

function setLevel(level: Level, levelView: LevelView, kind: LevelKind, name: string) {
  // Loading anything else (a built-in map, a map file, ...) leaves the builder and its play test.
  if (!builderLoad && boardReady && (editor.active || testing)) {
    if (editor.active) closeEditor();
    testing = null;
    updateBuilderPanels();
  }
  gr.setLevel(levelView);
  sim = new Simulation(level);
  sim.cv.airaccelerate = settings.airaccel;
  sim.autobhop = settings.autobhop;
  sim.cv.slopeSlide = settings.slopeSlide;
  levelKind = kind;
  levelName = name;
  mapSimLevel = null;
  spectating = null;
  mapAudio?.dispose();
  mapAudio = null;
  if (kind === 'course') {
    mapView = null;
    zones = null;
  }
  if (kind !== 'published') publishedCode = null;
  pb = getPB(settings, pbMap());
  pbSplits = getSplits(settings, pbMap());
  hud.setMapName(name);
  hud.hideFinish();
  void music.setPlaying(musicWanted());
  updateMusicHint();
  restart();
  sim.resyncYaw(view.yaw);
  updateBoard();
}

/* ---------------- Online hiscores ---------------- */

/** Board key of a loaded map: its id and its zones (players draw their own zones). */
function mapBoardKey(): string | null {
  if (!zones?.start || !zones.end || !levelName) return null;
  // Standard zones: the map's shared board. Your own zones: a board for those zones.
  if (zones.source === STANDARD_ZONES_NAME) return mapBoard(levelName);
  const z = JSON.stringify([zones.start, zones.end, zones.checkpoints]);
  let h = 0x811c9dc5;
  for (let i = 0; i < z.length; i++) h = Math.imul(h ^ z.charCodeAt(i), 0x01000193);
  return mapBoard(levelName, (h >>> 0).toString(16));
}

/**
 * A fresh simulation copy of the loaded map, for the replay bot (null on other levels). Keeps only
 * what the simulation needs, not the textures.
 */
let mapSimLevel: (() => Level) | null = null;

/** Set once the level builder exists (levels load before it). */
let boardReady = false;

/** Which hiscores the current level has (shown on Tab, posted to on finish). */
function updateBoard() {
  if (!boardReady) return;
  const rules = { airaccel: settings.airaccel, autobhop: settings.autobhop, slopeSlide: settings.slopeSlide };
  let key: string | null = null;
  let note = '';
  if (editor.active) note = 'No hiscores while building.';
  else if (levelKind === 'course') key = 'course';
  else if (levelKind === 'builtin') key = 'b:' + levelName;
  else if (levelKind === 'published' && publishedCode) key = 'p:' + publishedCode;
  else if (levelKind === 'custom') note = 'Publish this level to give it online hiscores.';
  else if (levelKind === 'map') {
    key = mapBoardKey();
    note = key
      ? "Times on loaded maps can't be checked by the server, and only players with the same zones share hiscores."
      : 'Draw a start and an end zone to get hiscores on this map.';
  }
  loadBot(key, rulesKey(rules));
  scoreboard.setBoard({
    key,
    title: levelKind === 'custom' || levelKind === 'published' ? (currentDef?.title ?? levelName) : levelName,
    rules: rulesKey(rules),
    rulesLabel: rulesLabel(rules),
    note,
  });
}

/* ---------------- Replay bot ---------------- */

const bot = new ReplayBot(gr.scene);
bot.enabled = settings.replayBot;
{
  const cb = $('sReplayBot') as HTMLInputElement;
  cb.checked = settings.replayBot;
  cb.addEventListener('change', () => {
    settings.replayBot = bot.enabled = cb.checked;
    saveSettings(settings);
  });
}
/** Board and rules the bot is for (what's loaded or loading). */
let botFor = '';
/**
 * Show the board's record run as the replay bot. Checked boards only (the server re-ran those runs,
 * and has their replays); `force` asks again (a new record).
 */
function loadBot(board: string | null, rules: string, force = false) {
  const id = board ? board + ' ' + rules : '';
  if (id === botFor && !force) return;
  botFor = id;
  bot.clear();
  botRecord = null;
  if (spectating) restart();
  updateSpectateHint();
  if (!board) return;
  // The bot runs on a copy of the level, so it never touches the one being played.
  const makeLevel = botLevel(board);
  if (!makeLevel) return;
  const stale = () => botFor !== id;
  online
    .topReplay(board, rules)
    .then(async ({ bot: top }) => {
      if (!top || stale()) return;
      const replay = decodeReplay(fromBase64Url(top.replay));
      const path = await ghostPath(makeLevel(), replay, stale);
      // Only a run that really finishes here in its posted time (loaded-map times aren't checked
      // by the server, and another version of the map would send the bot through walls).
      if (path && !stale() && path.timerTicks === top.ticks) {
        bot.set(path, top.name, 'WR ' + fmtTime(top.ticks * TICK_DT));
        // Its name tag is a new texture (and shader variant): shown once that's ready, not compiled
        // the moment it comes into view.
        bot.waiting = true;
        void gr.prewarm().then(() => (bot.waiting = false));
        botRecord = { replay, name: top.name, ticks: top.ticks };
        updateSpectateHint();
      }
    })
    .catch(() => {
      // No bot when the API can't be reached.
    });
}

/** Builds the bot's own copy of the current level, with the zones the board is for. */
function botLevel(board: string): (() => Level) | null {
  if (board === 'course') return buildCourse;
  if (levelKind === 'map') {
    const make = mapSimLevel;
    const z = zones;
    if (!make || !z) return null;
    return () => {
      const lvl = make();
      lvl.startZone = z.start;
      lvl.endZone = z.end;
      lvl.checkpointZones = z.checkpoints;
      lvl.stageCount = z.start ? z.checkpoints.length + 1 : 0;
      return lvl;
    };
  }
  const def = currentDef;
  return def ? () => buildBoxLevel(def) : null;
}

/* ---------------- Chat and commands ---------------- */

/** Y: type in the chat (playing, not in the builder). */
function openChat() {
  if (editor.active || touch) return;
  // Let go of every held key, so you don't keep running while typing.
  input.clear();
  chat.open();
}

chat.onSay = (text) => {
  chat.add(text, 'say', online.me?.name ?? 'You');
  if (text.startsWith('!') || text.startsWith('/')) runCommand(text.slice(1));
};

const COMMANDS_HELP =
  'Commands: !r !restart · !stage · !noclip · !top10 !hiscores · !knife · !usp · !skins · !ws · !replay !spec !spectate · !spiketest';

function runCommand(line: string) {
  const c = line.trim().split(/\s+/)[0].toLowerCase();
  switch (c) {
    case 'r':
    case 'restart':
      restart();
      break;
    case 'stage':
    case 'stages':
      stageMenu();
      break;
    case 'noclip':
      toggleNoclip();
      break;
    case 'top10':
    case 'top':
    case 'hiscores':
    case 'wr':
      chatTop10();
      break;
    case 'knife':
      // Dropped: get it back; otherwise pick a knife.
      if (!sim.weapons.hasKnife) giveWeapon('knife');
      else knifeMenu();
      break;
    case 'usp':
    case 'pistol':
      giveWeapon('usp');
      break;
    case 'skins':
    case 'skin':
      skinsMenu();
      break;
    case 'ws':
      wsMenu();
      break;
    case 'replay':
    case 'spectate':
    case 'spec':
      if (!canSpectate()) chat.add("Spectating isn't available in the level builder.", 'error');
      else if (!spectating && !botRecord) chat.add('No record to spectate on this map yet.', 'error');
      else toggleSpectate();
      break;
    case 'spiketest':
      startSpikeTest();
      break;
    case 'help':
    case 'commands':
      chat.add(COMMANDS_HELP);
      break;
    default:
      chat.add(`Unknown command !${c}. Type !help for the list.`, 'error');
  }
}

/* ---------------- Spike test (!spiketest) ---------------- */

/**
 * Lag spikes that aren't the game's own work (a frame shown one refresh late) depend on the PC: this
 * counts late frames for 10 s at a time with one part of the frame switched off, to see which matters.
 */
const SPIKE_PARTS = [
  'normal',
  'no HUD',
  'nothing on top',
  'half resolution',
  'no drawing',
  'normal',
] as const;
/** What "nothing on top" hid, with the visibility to put back. */
const spikeHidden = new Map<HTMLElement, string>();
const SPIKE_PART_MS = 10000;
let spikeTest: {
  part: number;
  start: number;
  frames: number;
  late: number;
  worst: number;
  results: string[];
} | null = null;

function startSpikeTest() {
  if (spikeTest) return;
  spikeTest = {
    part: 0,
    start: performance.now(),
    frames: 0,
    late: 0,
    worst: 0,
    results: [],
  };
  chat.add(
    `Spike test (low latency ${settings.lowLatency ? 'on' : 'off'}): ${SPIKE_PARTS.length} parts of 10 s. Play as usual (bhop around); the picture freezes in the "no drawing" part.`,
  );
  setSpikePart();
}

function setSpikePart() {
  const t = spikeTest;
  const part = t ? SPIKE_PARTS[t.part] : null;
  for (const el of document.querySelectorAll<HTMLElement>('.hud'))
    el.style.visibility = part === 'no HUD' ? 'hidden' : '';
  // Every page element over the game (HUD, chat, hints, music note...): only the canvas is left.
  for (const [el, v] of spikeHidden) el.style.visibility = v;
  spikeHidden.clear();
  if (part === 'nothing on top')
    for (const el of document.querySelectorAll<HTMLElement>('body > *, #view > *')) {
      if (el.contains(gr.canvas) || el.tagName === 'SCRIPT') continue;
      spikeHidden.set(el, el.style.visibility);
      el.style.visibility = 'hidden';
    }
  gr.resize();
  if (part === 'half resolution') {
    gr.renderer.setPixelRatio(gr.renderer.getPixelRatio() / 2);
    gr.renderer.setSize(gr.canvas.clientWidth, gr.canvas.clientHeight, false);
  }
}

/** Count this frame in the spike test (late: over 1.6 typical frames). */
function spikeTestFrame(now: number, frameMs: number) {
  const t = spikeTest;
  if (!t) return;
  const typical = debug.typicalFrame;
  if (frameMs < 100 && typical > 0) {
    t.frames++;
    if (frameMs > typical * 1.6) t.late++;
    t.worst = Math.max(t.worst, frameMs);
  }
  if (now - t.start < SPIKE_PART_MS) return;
  const line = `${SPIKE_PARTS[t.part]}: ${t.late} late of ${t.frames} frames (worst ${t.worst.toFixed(1)} ms)`;
  t.results.push(line);
  chat.add('Spike test · ' + line);
  t.part++;
  t.start = now;
  t.frames = t.late = t.worst = 0;
  if (t.part < SPIKE_PARTS.length) {
    setSpikePart();
    return;
  }
  spikeTest = null;
  setSpikePart();
  console.log(['Spike test', ...t.results].join('\n'));
  chat.add('Spike test done. Send a screenshot of these lines.');
}

/** !knife / !usp: the weapon back in hand (through the command, so replays have it too). */
function giveWeapon(w: 'knife' | 'usp') {
  if (spectating) {
    chat.add('Stop spectating first (L).', 'error');
    return;
  }
  input.queueWeapon(w === 'knife' ? SELECT_GIVE_KNIFE : SELECT_GIVE_USP);
}

/** Noclip on or off. Flying ends the run: the timer stays off until you restart. */
function toggleNoclip() {
  if (spectating) {
    chat.add('Stop spectating first (L).', 'error');
    return;
  }
  sim.setNoclip(!sim.noclip);
  if (sim.noclip) {
    sim.timerOff = true;
    recorder.invalidate();
  }
  const msg = sim.noclip
    ? 'Noclip on. The timer is off until you restart (!r).'
    : 'Noclip off. Restart (!r) to get the timer back.';
  chat.add(msg);
  hud.toast(msg, 2000);
}

/** Where stage `n` (1 = the start) puts you: the level's stage spawns, or a stage zone's middle. */
function stageSpawns(): SpawnPoint[] {
  const lvl = sim.level;
  const out: SpawnPoint[] = [lvl.spawn];
  if (lvl.stageSpawns) out.push(...lvl.stageSpawns);
  else
    for (const z of lvl.checkpointZones ?? []) {
      const pos: [number, number, number] = [
        (z.min[0] + z.max[0]) / 2,
        (z.min[1] + z.max[1]) / 2,
        Math.max(z.min[2] + 1, z.max[2] - 73),
      ];
      // Up out of anything solid.
      for (
        let i = 0;
        i < 24 && lvl.world.testPosition(pos, HULL_MINS, HULL_MAXS_STAND, MASK_PLAYERSOLID);
        i++
      )
        pos[2] += 8;
      out.push({ pos, yaw: lvl.spawn.yaw, stage: out.length });
    }
  return out;
}

function stageMenu() {
  const spawns = stageSpawns();
  if (spawns.length < 2) chat.add('This level has one stage: the start.');
  radio.show(
    'Teleport to stage',
    spawns.map((sp, i) => ({
      label: i === 0 ? 'Stage 1 (start)' : `Stage ${i + 1}`,
      current: sim.stage === i + 1,
      onPick: () => teleportToStage(i + 1, sp),
    })),
    Math.floor(Math.max(0, sim.stage - 1) / 7),
  );
}

/** Practice: put the player at a stage. The run stops; the timer comes back with a restart. */
function teleportToStage(n: number, sp: SpawnPoint) {
  if (spectating) restart();
  if (sim.noclip) sim.setNoclip(false);
  sim.timer.reset();
  sim.armed = false;
  sim.timerOff = true;
  sim.splits.length = 0;
  recorder.invalidate();
  sim.teleport(sp);
  sim.spawn = sp;
  sim.stage = n;
  sim.events |= EV_HIDE_FINISH;
  handleSimEvents();
  chat.add(`Teleported to stage ${n}. The timer is off: type !r to restart.`);
}

/** !top10: the board's best times, in the chat. */
function chatTop10() {
  const key = scoreboard.currentKey;
  if (!key) {
    chat.add('This level has no hiscores.', 'error');
    return;
  }
  const rules = currentRules();
  online
    .board(key, rulesKey(rules), 10)
    .then((b) => {
      if (!b.top.length) {
        chat.add(`No times on ${levelTitle()} with ${rulesLabel(rules)} yet. Be the first!`);
        return;
      }
      chat.add(`Top ${b.top.length} · ${levelTitle()} · ${rulesLabel(rules)}`);
      for (const t of b.top) chat.add(`#${t.rank}  ${t.name}  ${fmtTime(t.ticks * TICK_DT)}`);
    })
    .catch(() => chat.add("Couldn't reach the hiscores right now.", 'error'));
}

const levelTitle = () =>
  levelKind === 'custom' || levelKind === 'published' ? (currentDef?.title ?? levelName) : levelName;

function knifeMenu() {
  const items: MenuItem[] = (Object.keys(KNIVES) as KnifeKind[]).map((k) => ({
    label: KNIVES[k].label,
    current: settings.knifeType === k,
    onPick: () => {
      setKnifeKind(k);
      chat.add(`${KNIVES[k].label} equipped.`);
    },
  }));
  radio.show('Knife', items);
}

function skinsMenu() {
  const names = Object.keys(FINISHES) as Finish[];
  const at = Math.max(0, names.indexOf(settings.finish));
  radio.show(
    'Skins',
    names.map((f) => ({
      label: FINISHES[f].label,
      current: settings.finish === f,
      onPick: () => {
        setFinish(f);
        chat.add(`Skin: ${FINISHES[f].label}.`);
      },
    })),
    Math.floor(at / 7),
  );
}

function wsMenu() {
  radio.show('Weapon skins', [
    { label: 'Knife', onPick: knifeMenu },
    { label: 'Skin', onPick: skinsMenu },
  ]);
}

function onlineLine(msg: string) {
  const el = $('finOnline');
  el.textContent = msg;
  el.hidden = !msg;
}

/** A run finished: keep it as the level's proof (own levels) or post it to the hiscores. */
function submitRun() {
  onlineLine('');
  const ticks = sim.timer.ticks;
  // Own levels: remember the run, it's what lets the level be published. Replays are compressed in
  // a worker, so finishing never stalls the game.
  if (levelKind === 'custom' && currentDef && recorder.valid && !editor.active) {
    const def = currentDef;
    const key = levelKey(def);
    void encodeReplayAsync(recorder.snapshot()!)
      .then((replay) => saveProof(def.name, { key, replay, ticks }))
      .catch(() => {});
    onlineLine('You finished your level, so you can publish it now (Map Builder tab).');
    return;
  }
  const key = scoreboard.currentKey;
  if (!key) return;
  if (!online.me) {
    onlineLine('Pick a name in Settings to post your times to the online hiscores.');
    return;
  }
  const best = scoreboard.myBest;
  if (best !== null && ticks >= best) {
    onlineLine(`Online best ${fmtTime(best * TICK_DT)} · hold Tab for the hiscores`);
    return;
  }
  const checked = levelKind !== 'map';
  const replay = recorder.snapshot();
  if (!replay) {
    onlineLine('Not posted online: noclip was used or the run was too long. Restart for a new run.');
    return;
  }
  onlineLine('Posting to the hiscores…');
  const req = checked
    ? encodeReplayAsync(replay).then((bytes) => online.submitChecked(key, bytes))
    : encodeReplayAsync(replay).then((bytes) =>
        online.submitUnchecked(
          key,
          rulesKey({ airaccel: sim.cv.airaccelerate, autobhop: sim.autobhop, slopeSlide: sim.cv.slopeSlide }),
          ticks,
          sim.stats.jumps,
          sim.stats.syncPercent,
          bytes,
        ),
      );
  req
    .then((r) => {
      if (r.improved && r.rank === 1) loadBot(key, botFor.split(' ')[1] ?? '', true);
      onlineLine(
        r.improved
          ? `Online rank #${r.rank ?? '?'} · hold Tab for the hiscores`
          : `Online best ${fmtTime(r.best * TICK_DT)} (rank #${r.rank ?? '?'})`,
      );
      scoreboard.refresh();
    })
    .catch((e: Error) => onlineLine(e.message));
}

/** Music plays on the built-in course and the built-in maps. */
function musicWanted() {
  return (
    levelKind === 'course' ||
    levelKind === 'builtin' ||
    // Loaded maps get our music unless they bring their own.
    (levelKind === 'map' && mapAudio !== null && !mapAudio.hasMusic) ||
    (boardReady && (editor.active || testing !== null))
  );
}
music.onTrack = (t) => {
  debug.note('music');
  const a = $('npTrack') as HTMLAnchorElement;
  a.textContent = t.title;
  // Our own songs have no page to link to.
  if (t.link) a.href = t.link;
  else a.removeAttribute('href');
  $('npArtist').textContent = t.link
    ? `by ${t.artist} · ${t.genre} · on Audius`
    : `by ${t.artist} · ${t.genre}`;
  if (lock.locked) hud.toast(`♪ ${t.title} · ${t.artist}`, 3000);
  updateMusicHint();
  if (boardReady && editor.active) editor.refreshPanel();
};

/** Music on or off (the mute key, or tapping the music button on touch screens). */
function toggleMusic() {
  settings.musicMuted = !settings.musicMuted;
  // Turning it on at 0% volume would stay silent: use the default level.
  if (!settings.musicMuted && settings.musicVolume === 0) {
    settings.musicVolume = 10;
    music.setVolume(0.1);
    ($('sMusic') as HTMLInputElement).value = '10';
    $('musicVal').textContent = '10';
  }
  saveSettings(settings);
  music.setMuted(settings.musicMuted);
  if (!settings.musicMuted && musicWanted()) void music.setPlaying(true);
  hud.toast(settings.musicMuted ? 'Music off' : 'Music on', 1500);
  updateMusicHint();
  if (editor.active) editor.refreshPanel();
}

/** In-game note on the built-in maps: how to turn the music on, or what's playing and how to stop it. */
/** In game: "P · change skin" above the music note (keyboard players, while the knife shows). */
function updateSkinHint() {
  const el = $('skinHint');
  const key = settings.binds.randomFinish[0];
  const show = lock.locked && !touch && !editor.active && !testing && gr.showKnife && !!key;
  el.hidden = !show;
  if (show) el.textContent = `Press ${inputName(key)} to change skin`;
}

/** Spectating is for playing levels, not the builder or its play test. */
const canSpectate = () => !editor.active && !testing;

/** "Press L to spectate", or while spectating, who you're watching. */
function updateSpectateHint() {
  if (!boardReady) return;
  const key = settings.binds.spectate[0];
  const name = key ? inputName(key) : null;
  const rec = spectating?.rec ?? botRecord;
  const inGame = lock.locked && canSpectate() && !!rec;
  const hint = $('specHint');
  hint.hidden = !inGame || !!spectating || touch || !name;
  if (!hint.hidden) hint.textContent = `Press ${name} to spectate the WR (${rec!.name})`;
  const bar = $('specBar');
  bar.hidden = !spectating;
  if (spectating)
    bar.textContent = `Spectating ${spectating.rec.name} · WR ${fmtTime(spectating.rec.ticks * TICK_DT)}${
      name ? ` · ${name} to stop` : ''
    }`;
}

/** Watch the record run (L), or stop watching. */
function toggleSpectate() {
  if (spectating) {
    restart();
    hud.toast('Back to your run', 1500);
    return;
  }
  if (!canSpectate()) return;
  if (!botRecord) {
    hud.toast('No record to spectate on this map yet', 2000);
    return;
  }
  startSpectate(botRecord);
}

function startSpectate(rec: { replay: Replay; name: string; ticks: number }) {
  const r = rec.replay;
  // The record's rules, as the replay was played with them.
  sim.cv.airaccelerate = r.airaccel;
  sim.autobhop = r.autobhop;
  sim.cv.slopeSlide = r.slopeSlide;
  sim.restart();
  sim.player.oldJump = r.jumpHeld;
  recorder.invalidate();
  handleSimEvents();
  // Skip the time in the start zone: play it at once, silently, up to where the timer starts.
  let i = 0;
  while (i < r.ticks && sim.timer.state !== 'run') {
    sim.tick(replayCmd(r, i++, cmd, sim.cv.forwardspeed));
    sim.events = 0;
    sim.weapons.events = 0;
  }
  sim.player.fx.snapYaw = null;
  const yaw = i > 0 ? r.yaw[i - 1] : r.yaw[0];
  const pitch = i > 0 ? r.pitch[i - 1] : r.pitch[0];
  view.yaw = yaw;
  view.pitch = pitch;
  spectating = { rec, i, yaw0: yaw, pitch0: pitch, yaw1: yaw, pitch1: pitch };
  updateSpectateHint();
}

/** The next command of the spectated run; after the end zone it starts again. */
function spectateCmd(out: typeof cmd) {
  let s = spectating!;
  if (s.i >= s.rec.replay.ticks) {
    startSpectate(s.rec);
    s = spectating!;
  }
  s.yaw0 = s.yaw1;
  s.pitch0 = s.pitch1;
  replayCmd(s.rec.replay, s.i++, out, sim.cv.forwardspeed);
  s.yaw1 = out.yaw;
  s.pitch1 = out.pitch;
  return out;
}

function updateMusicHint() {
  updateSkinHint();
  updateSpectateHint();
  chat.setShown(lock.locked && !editor.active);
  if (!lock.locked || editor.active) radio.close();
  const el = $('musicHint');
  const show = lock.locked && musicWanted() && !editor.active;
  el.hidden = !show;
  if (!show) return;
  const key = settings.binds.mute[0] ? inputName(settings.binds.mute[0]) : null;
  const t = music.current;
  el.classList.toggle('off', settings.musicMuted);
  el.textContent = settings.musicMuted
    ? touch
      ? '♪ Tap to listen to music'
      : key
        ? `♪ Press ${key} to listen to music`
        : '♪ Music is off (Settings, Controls: Mute music)'
    : `♪ ${t ? `${t.title} · ${t.artist}` : 'Music on'}${touch ? ' · tap to stop' : key ? ` · ${key} to stop` : ''}`;
}
$('musicHint').addEventListener('click', () => toggleMusic());
music.onError = (msg) => ($('npArtist').textContent = msg);

/** PBs of loaded maps are kept per map name. */
const pbMap = () => (levelKind === 'course' ? undefined : levelName);
/** Split times of the PB run, for comparing stages. */
let pbSplits: number[] | null = null;

/** Play a box level (a built-in map or one made in the level builder). */
let boxView: BoxLevelView | null = null;
function startBoxLevel(def: LevelDef, kind: 'builtin' | 'custom' | 'published') {
  currentDef = def;
  const level = buildBoxLevel(def);
  // While building, the next view reuses this one's materials.
  if (boxView) boxView.keepMaterials = editor.active;
  // (The first level starts before the builder exists: not building then.)
  const v = new BoxLevelView(level, gr.anisotropy, !(boxView && editor.active));
  v.onAssetsLoaded = () => gr.prewarm();
  setLevel(level, v, kind, def.name);
  boxView = v;
}

function refreshBuiltins() {
  mainMenu.setBuiltins(
    BUILTIN_MAPS.map((m) => {
      const t = getPB(settings, m.name);
      const def = builtinLevel(m.name)!;
      return {
        ...m,
        color: def.boxes.find((b) => b.kind === 'block')?.color ?? '#888',
        pb: t === null ? null : fmtTime(t),
      };
    }),
  );
  updatePlayButton();
}

function startCourse() {
  const course = buildCourse();
  setLevel(course, new CourseView(course, gr.anisotropy), 'course', course.name);
}
void startCourse;
/** The map you start on and that Play starts: the easiest built-in map. */
const DEFAULT_MAP = 'bhop_meadow';
const LAST_MAP_KEY = 'bhop-web-last-map';
/** The map the Play button plays: the built-in map you picked last (remembered), else Meadow. */
let playMap = DEFAULT_MAP;
try {
  const saved = localStorage.getItem(LAST_MAP_KEY);
  if (saved && builtinLevel(saved)) playMap = saved;
} catch {
  // Storage blocked: Meadow.
}
startBoxLevel(builtinLevel(playMap)!, 'builtin');

/** Show the Play map on the button, and mark its card in the map list. */
function updatePlayButton() {
  const m = BUILTIN_MAPS.find((x) => x.name === playMap);
  if (!m) return;
  const pb = getPB(settings, m.name);
  const btn = $('playCourse');
  btn.textContent = '';
  const b = document.createElement('b');
  b.textContent = '▶ Play ' + m.title;
  const sub = document.createElement('span');
  sub.textContent =
    '★'.repeat(m.difficulty) +
    '☆'.repeat(5 - m.difficulty) +
    ` · ${m.stages} stages · ${pb === null ? 'not finished yet' : 'PB ' + fmtTime(pb)} · or pick a map below`;
  btn.append(b, sub);
  for (const li of document.querySelectorAll<HTMLElement>('#builtinList li'))
    li.classList.toggle('current', li.dataset.map === playMap);
}

function handleSimEvents() {
  const ev = sim.events;
  sim.events = 0;
  if (sim.timer.state !== lastTimerState) {
    lastTimerState = sim.timer.state;
    if (lastTimerState === 'run') debug.note('run start');
  }
  if (ev & EV_HIDE_FINISH) hud.hideFinish();
  if (ev & EV_FINISHED) finish();
  if (ev & EV_STAGE) {
    showSplit();
    debug.note('stage');
  }
  if (ev & EV_TELEPORTED) debug.note('respawn');
  if (ev & EV_FINISHED) debug.note('finish');
  if (ev & EV_JUMPED) sound.jump();
  if (ev & EV_LANDED) sound.land(sim.player.landSpeed);
  if (ev & EV_BROKE) sound.glass();
  else if (ev & EV_KNIFE_HIT) {
    const k = sim.weapons.knife;
    sound.knifeHit(sim.level.runtime?.surface?.(k.solid, k.pos) ?? 'concrete');
  }
  const fx = sim.player.fx;
  if (fx.snapYaw !== null) {
    view.yaw = fx.snapYaw;
    view.pitch = 0;
    fx.snapYaw = null;
  }
}

/** Entered a stage: show its split, and the difference to the PB run's split. */
function showSplit() {
  const i = sim.splits.length - 1;
  if (i < 0) return;
  const t = sim.splits[i] * TICK_DT;
  const ref = pbSplits?.[i];
  const diff = ref === undefined ? '' : ` (${t <= ref ? '−' : '+'}${Math.abs(t - ref).toFixed(2)})`;
  hud.toast(`Stage ${i + 2} · ${fmtTime(t)}${diff}`, 2500);
}

function finish() {
  if (spectating) {
    hud.toast(`${spectating.rec.name} finished · WR ${fmtTime(sim.timer.finished)}`, 2500);
    return;
  }
  sound.win();
  const t = sim.timer.finished;
  const prev = pb;
  const isPB = prev === null || t < prev;
  if (isPB) {
    setPB(settings, t, pbMap());
    pb = t;
    pbSplits = sim.splits.map((x) => x * TICK_DT);
    setSplits(settings, pbSplits, pbMap());
    if (levelKind === 'builtin') refreshBuiltins();
  }
  submitRun();
  const restartKey = settings.binds.restart[0];
  hud.showFinish(
    isPB ? 'New personal best' : 'Map finished',
    t,
    sim.stats.jumps +
      ' jumps · ' +
      sim.stats.syncPercent +
      '% sync' +
      (isPB || prev === null ? '' : ' · PB ' + fmtTime(prev)) +
      (restartKey ? ' · ' + inputName(restartKey) + ' to restart' : ''),
  );
}

function restart() {
  if (spectating) {
    spectating = null;
    sim.cv.airaccelerate = settings.airaccel;
    sim.autobhop = settings.autobhop;
    sim.cv.slopeSlide = settings.slopeSlide;
    updateSpectateHint();
  }
  sim.restart();
  recorder.start(
    { airaccel: sim.cv.airaccelerate, autobhop: sim.autobhop, slopeSlide: sim.cv.slopeSlide },
    sim.player.oldJump,
  );
  // The run's first command says which weapons you have, so its replay starts with the same.
  input.queueWeapon(sim.weapons.loadoutCode, true);
  handleSimEvents();
}

/* ---------------- The player's own CS:S files ---------------- */

const gameFolder = new GameFolder();
let currentMap: CachedMap | null = null;
function updateGameStatus(msg = '') {
  $('gameStatus').textContent =
    msg ||
    (gameFolder.name
      ? gameFolder.ready
        ? `using "${gameFolder.name}"`
        : `"${gameFolder.name}"`
      : 'not set');
  $('gameForget').hidden = !gameFolder.name;
  if (weaponModels === 'ready' && !msg) $('gameStatus').textContent += ' · CS:S weapon models loaded';
  if (gameSoundCount && !msg) $('gameStatus').textContent += ' · CS:S sounds loaded';
}

/**
 * The real CS:S knife and USP: from weapon skin packs, the browser cache, or read once from the game
 * folder. Loads run one after another.
 */
let weaponModels: 'none' | 'ready' = 'none';
/** CS:S sound files loaded (0: Kenney/synthesised sounds play). */
let gameSoundCount = 0;
let weaponLoads: Promise<void> = Promise.resolve();
function loadWeaponModels(
  game: GameVpk[] | null,
  opts: { packs?: { name: string; data: Uint8Array }[]; force?: boolean } = {},
) {
  weaponLoads = weaponLoads.then(async () => {
    if (weaponModels === 'ready' && !opts.packs && !opts.force) return;
    const mod = await import('./models/loadWeapons');
    if (opts.packs) $('skinStatus').textContent = 'loading…';
    const { assets, sounds, error } = await mod.loadCssWeapons(game, opts.packs ?? null);
    if (error) {
      $('skinStatus').textContent = error;
      return;
    }
    if (sounds) {
      gameSoundCount = sounds.size;
      sound.setGameSounds(sounds);
    }
    weaponModels = assets ? 'ready' : 'none';
    gr.viewmodel.setStudioWeapons(assets);
    if (sim.weapons.active) gr.viewmodel.setWeapon(sim.weapons.active, 0);
    gr.viewmodel.setSilenced(sim.weapons.silenced);
    gr.prewarm();
    updateGameStatus();
    await updateSkinStatus();
  });
  return weaponLoads;
}
async function updateSkinStatus() {
  const skins = await loadSkinFiles().catch(() => null);
  const models = (skins ?? []).filter(([p]) => p.endsWith('.mdl')).map(([p]) => p.split('/').pop());
  const textures = (skins ?? []).filter(([p]) => p.endsWith('.vtf')).length;
  $('skinStatus').textContent = skins
    ? models.length
      ? models.join(', ') + (textures ? ` + ${textures} textures` : '')
      : `${textures} texture${textures === 1 ? '' : 's'}`
    : 'none';
  $('skinRemove').hidden = !skins;
}
void updateSkinStatus();
void loadWeaponModels(null);
$('skinAdd').addEventListener('click', () => ($('skinInput') as HTMLInputElement).click());
$('skinInput').addEventListener('change', async () => {
  const input = $('skinInput') as HTMLInputElement;
  const files = [...(input.files ?? [])];
  input.value = '';
  if (!files.length) return;
  const packs = await Promise.all(
    files.map(async (f) => ({ name: f.name, data: new Uint8Array(await f.arrayBuffer()) })),
  );
  await loadWeaponModels(await gameFolder.files(false), { packs });
});
$('skinRemove').addEventListener('click', async () => {
  const mod = await import('./models/loadWeapons');
  await mod.removeWeaponSkins();
  await loadWeaponModels(await gameFolder.files(false), { force: true });
});
$('gamePathCopy').addEventListener('click', () => {
  void navigator.clipboard?.writeText($('gamePathText').textContent ?? '').then(() => {
    $('gamePathCopy').textContent = 'Copied';
    setTimeout(() => ($('gamePathCopy').textContent = 'Copy'), 1500);
  });
});
gameFolder.restore().then(async () => {
  updateGameStatus();
  await weaponLoads;
  if (gameSoundCount === 0 && gameFolder.ready) {
    const files = await gameFolder.files(false);
    if (files) void loadWeaponModels(files, { force: true });
  }
});
$('gameChoose').addEventListener('click', async () => {
  const msg = await gameFolder.choose($('gameDirInput') as HTMLInputElement);
  updateGameStatus(msg);
  if (!msg && gameFolder.ready) void loadWeaponModels(await gameFolder.files(false), { force: true });
  // Reload the current map so it picks up the game's textures.
  if (!msg && gameFolder.ready && levelKind === 'map' && currentMap) mainMenu.onPlayCached(currentMap);
});
$('gameForget').addEventListener('click', async () => {
  await gameFolder.forget();
  await clearModelFiles().catch(() => {});
  gr.viewmodel.setStudioWeapons(null);
  weaponModels = 'none';
  updateGameStatus();
});

/* ---------------- Map loading (lazy chunk) ---------------- */

let loadSeq = 0;

async function refreshMapList() {
  try {
    mainMenu.setMaps(await listMaps());
  } catch {
    mainMenu.setMaps(null);
  }
}
refreshMapList();

/** Load a map file (from the picker, a drop, or the cache) and switch to it. */
async function startLoad(
  fileName: string,
  data: ArrayBuffer,
  cached?: CachedMap,
  extras?: [string, Uint8Array][],
) {
  const seq = ++loadSeq;
  mainMenu.startLoading(cached ? cached.name : fileName);
  currentMap = cached ?? null;
  try {
    const game = await gameFolder.files(true);
    if (game) void loadWeaponModels(game);
    const mod = await import('./maps/bsp/bspLevel');
    if (seq !== loadSeq) return;
    const loaded = await mod.loadMapFile(
      fileName,
      data,
      {
        onProgress: (stage, f) => seq === loadSeq && mainMenu.progress(stage, f),
        onPick: (names) => mainMenu.pick(names),
      },
      gr.supportsS3TC,
      game ?? undefined,
      extras,
    );
    if (seq !== loadSeq) return;
    mainMenu.progress('Preparing the map', 1);
    const bl = mod.createBspLevel(loaded, gr.anisotropy);
    setLevel(bl.level, bl.view, 'map', bl.name);
    {
      const m = loaded.map;
      const simData = {
        collision: m.collision,
        triggers: m.triggers,
        entities: m.entities,
        brushModels: m.brushModels,
        spawns: m.spawns,
        mins: m.mins,
        maxs: m.maxs,
      } as Parameters<typeof mod.createBspSimLevel>[0];
      mapSimLevel = () => mod.createBspSimLevel(simData).level;
    }
    mapView = bl.view;
    sound.unlock();
    mapAudio = new MapAudio(sound, bl.runtime, bl.sounds);
    void music.setPlaying(musicWanted());
    updateMusicHint();
    const saved = await getZones(bl.name).catch(() => null);
    zones = saved ?? emptyZones(bl.name);
    applyZones();
    // No zones yet for this map: use the standard ones bhop servers use, if there are any.
    if (!saved) void loadStandardZones(bl.name, false);

    let note = bl.warnings.join(' ');
    try {
      if (cached) {
        await touchMap(cached.id);
        await addMapExtras(cached.id, loaded.extras);
      } else
        await saveMap(
          {
            id: mapId(bl.name, loaded.hash),
            name: bl.name,
            sourceName: fileName,
            size: loaded.bsp.byteLength,
          },
          loaded.bsp,
          loaded.extras,
        );
    } catch {
      note = (
        note + " This browser couldn't keep the map, so you'll need to load the file again next time."
      ).trim();
    }
    refreshMapList();
    mainMenu.setPaused('map', bl.name, note);
  } catch (e) {
    if (seq !== loadSeq || (e as Error).message === 'Cancelled') return;
    mainMenu.error((e as Error).message || String(e));
  }
}

async function loadFile(file: File) {
  if (lock.locked) return;
  if (file.size > 512 * 1024 * 1024) {
    mainMenu.startLoading(file.name);
    mainMenu.error('That file is larger than 512 MB, which is too big for a map.');
    return;
  }
  let data: ArrayBuffer;
  try {
    data = await file.arrayBuffer();
  } catch {
    mainMenu.startLoading(file.name);
    mainMenu.error("Couldn't read that file.");
    return;
  }
  startLoad(file.name, data);
}

/** The big Play button: the default map. */
mainMenu.onPlayCourse = () => mainMenu.onPlayBuiltin(playMap);
mainMenu.onPlayBuiltin = (name) => {
  const def = builtinLevel(name);
  if (!def) return;
  // The Play button follows the map you picked.
  playMap = name;
  try {
    localStorage.setItem(LAST_MAP_KEY, name);
  } catch {
    // Not remembered.
  }
  updatePlayButton();
  if (levelKind !== 'builtin' || levelName !== name) startBoxLevel(def, 'builtin');
  else restart();
  mainMenu.setPaused('builtin', def.title);
  startPlaying();
};
refreshBuiltins();
mainMenu.onPickFile = loadFile;
mainMenu.onPlayCached = async (m) => {
  try {
    const file = await loadMapFile(m.id);
    if (!file) throw new Error('missing');
    startLoad(m.name + '.bsp', file.data, m, file.extras);
  } catch {
    mainMenu.startLoading(m.name);
    mainMenu.error("This map's file is gone from the browser's storage. Load it again from your computer.");
    deleteMap(m.id).finally(refreshMapList);
  }
};
mainMenu.onDeleteCached = (m) => {
  deleteMap(m.id).finally(refreshMapList);
};
mainMenu.onBack = () => {
  loadSeq++;
  import('./maps/bsp/bspLevel').then((m) => m.cancelLoad());
  mainMenu.show('home');
};
mainMenu.onHome = () => {
  if (editor.active) closeEditor();
  testing = null;
  mainMenu.show('home');
  updateBuilderPanels();
  void refreshLevels();
};

// Drag and drop a map anywhere while the menu is open.
let dragDepth = 0;
const hasFiles = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes('Files');
window.addEventListener('dragenter', (e) => {
  if (lock.locked || !hasFiles(e)) return;
  dragDepth++;
  $('dropZone').hidden = false;
});
window.addEventListener('dragleave', () => {
  if (--dragDepth <= 0) {
    dragDepth = 0;
    $('dropZone').hidden = true;
  }
});
window.addEventListener('dragover', (e) => {
  if (!hasFiles(e)) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = lock.locked ? 'none' : 'copy';
});
window.addEventListener('drop', (e) => {
  e.preventDefault();
  dragDepth = 0;
  $('dropZone').hidden = true;
  const file = e.dataTransfer?.files?.[0];
  if (file) loadFile(file);
});

/* ---------------- Timer zones (loaded maps) ---------------- */

/** Zone editor: first corner, then the zone waiting for a type (1 start, 2 end, 3 checkpoint). */
let zoneCorner: number[] | null = null;
let zonePending: Zone | null = null;
const zoneKey = () => (settings.binds.zone[0] ? inputName(settings.binds.zone[0]) : 'the zone key');

/** Push the current zones into the simulation, the view and the menu, and reset the run. */
function applyZones() {
  if (!zones || levelKind !== 'map') return;
  const lvl = sim.level;
  lvl.startZone = zones.start;
  lvl.endZone = zones.end;
  lvl.checkpointZones = zones.checkpoints;
  lvl.stageCount = zones.start ? zones.checkpoints.length + 1 : 0;
  sim.timer.reset();
  sim.armed = false;
  sim.stage = 1;
  hud.hideFinish();
  mapView?.zones.set([
    ...(zones.start ? [{ zone: zones.start, kind: 'start' as const }] : []),
    ...(zones.end ? [{ zone: zones.end, kind: 'end' as const }] : []),
    ...zones.checkpoints.map((zone) => ({ zone, kind: 'checkpoint' as const })),
  ]);
  const n = zones.checkpoints.length;
  const any = zones.start || zones.end || n;
  $('zoneStatus').textContent = any
    ? [
        zones.start ? 'Start ✓' : 'No start',
        zones.end ? 'End ✓' : 'No end',
        `${n} checkpoint${n === 1 ? '' : 's'}`,
        ...(zones.source ? [`from ${zones.source}`] : []),
      ].join(' · ')
    : 'No zones yet.';
  $('zoneHelp').textContent =
    `While playing, press ${zoneKey()} at one corner of a zone, walk to the opposite corner and press it again, ` +
    'then 1 = start, 2 = end, 3 = checkpoint. The timer runs from leaving the start zone to entering the end zone.';
  updateBoard();
}

/**
 * Fetch the standard zones (zones-cstrike) for the map that's loaded and use them. `replace`: also
 * when the map already has zones (the player asked for them).
 */
async function loadStandardZones(map: string, replace: boolean) {
  const found = await fetchStandardZones(map);
  // Still the same map, and (unless asked) the player hasn't drawn zones meanwhile.
  if (levelKind !== 'map' || levelName !== map || !zones) return;
  if (!found) {
    if (replace) $('mapNote').textContent = `No standard zones found for ${map}.`;
    return;
  }
  if (!replace && (zones.start || zones.end || zones.checkpoints.length)) return;
  zones = found;
  applyZones();
  await storeZones();
  hud.toast(`Timer zones for ${map} loaded from ${STANDARD_ZONES_NAME}.`, 3000);
  if (replace) $('mapNote').textContent = 'Standard zones loaded.';
}

async function storeZones() {
  if (!zones) return;
  try {
    await saveZones(zones);
  } catch {
    hud.toast("Couldn't save zones in this browser. Export them to keep them.", 5000);
  }
}

function onZoneKey() {
  if (levelKind !== 'map' || !zones) {
    hud.toast('Zones are fixed on the built-in course. Load a map to draw your own.');
    return;
  }
  if (zonePending) {
    zonePending = null;
    hud.toast('Zone cancelled.');
  } else if (!zoneCorner) {
    zoneCorner = [...sim.player.pos];
    hud.toast(`Corner 1 set. Walk to the opposite corner and press ${zoneKey()}.`, 0);
  } else {
    zonePending = zoneFromCorners(zoneCorner, sim.player.pos);
    zoneCorner = null;
    hud.toast(`Make this a zone: 1 start · 2 end · 3 checkpoint · ${zoneKey()} cancels`, 0);
  }
}

function assignZone(kind: 1 | 2 | 3) {
  if (!zones || !zonePending) return;
  zones.source = undefined;
  if (kind === 1) zones.start = zonePending;
  else if (kind === 2) zones.end = zonePending;
  else zones.checkpoints.push(zonePending);
  zonePending = null;
  hud.toast(
    kind === 1
      ? 'Start zone saved.'
      : kind === 2
        ? 'End zone saved.'
        : `Checkpoint ${zones.checkpoints.length} saved.`,
  );
  applyZones();
  storeZones();
}

$('zoneExport').addEventListener('click', () => {
  if (!zones) return;
  const url = URL.createObjectURL(new Blob([zonesToJson(zones)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${zones.map}.zones.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
});
$('zoneImport').addEventListener('click', () => {
  const inp = $('zoneFile') as HTMLInputElement;
  inp.value = '';
  inp.click();
});
$('zoneFile').addEventListener('change', async () => {
  const file = ($('zoneFile') as HTMLInputElement).files?.[0];
  if (!file || !zones) return;
  try {
    const z = zonesFromJson(await file.text());
    const other = z.map && z.map.toLowerCase() !== levelName.toLowerCase();
    zones = { ...z, map: levelName };
    applyZones();
    await storeZones();
    $('mapNote').textContent = other ? `Imported zones that were made for ${z.map}.` : 'Zones imported.';
  } catch (e) {
    $('mapNote').textContent = (e as Error).message;
  }
});
$('zoneStandard').addEventListener('click', () => {
  if (levelKind !== 'map') return;
  $('mapNote').textContent = 'Looking for standard zones…';
  void loadStandardZones(levelName, true);
});
$('zoneClear').addEventListener('click', () => {
  const b = $('zoneClear');
  if (!b.classList.contains('confirm')) {
    // Two clicks, so a stray click doesn't wipe the zones.
    b.classList.add('confirm');
    b.textContent = 'Clear all zones?';
    setTimeout(() => {
      b.classList.remove('confirm');
      b.textContent = 'Clear zones';
    }, 3000);
    return;
  }
  b.classList.remove('confirm');
  b.textContent = 'Clear zones';
  if (!zones) return;
  zones = emptyZones(levelName);
  applyZones();
  deleteZones(levelName).catch(() => {});
});

/* ---------------- Settings ---------------- */

/** Change the knife finish, save it and say which one it is. */
/** Hold another knife (Settings, !knife), save it and say which one it is. */
function setKnifeKind(k: KnifeKind) {
  settings.knifeType = k;
  saveSettings(settings);
  void gr.viewmodel.setKnife(k);
  ($('sKnifeType') as HTMLSelectElement).value = k;
  hud.toast(`Knife: ${KNIVES[k].label}`, 1500);
}
{
  const sel = $('sKnifeType') as HTMLSelectElement;
  for (const [id, k] of Object.entries(KNIVES)) sel.add(new Option(k.label, id));
  sel.value = settings.knifeType;
  sel.addEventListener('change', () => setKnifeKind(sel.value as KnifeKind));
  void gr.viewmodel.setKnife(settings.knifeType);
}

function setFinish(f: Finish) {
  settings.finish = f;
  saveSettings(settings);
  gr.viewmodel.setFinish(f);
  chat.setSkin(f);
  menu.showFinish(f);
  hud.toast(`Skin applied: ${FINISHES[f].label}`, 1500);
}
menu.onRandomFinish = () => setFinish(randomFinish(settings.finish));

function toggleDebug() {
  settings.debug = !debug.isVisible;
  debug.setVisible(settings.debug);
  saveSettings(settings);
}

input.onImpulse = (a: Action) => {
  switch (a) {
    case 'restart':
      restart();
      break;
    case 'inspect':
      gr.viewmodel.inspect();
      break;
    case 'randomFinish':
      setFinish(randomFinish(settings.finish));
      break;
    case 'spectate':
      toggleSpectate();
      break;
    case 'chat':
      openChat();
      break;
    case 'debug':
      toggleDebug();
      break;
    case 'zone':
      onZoneKey();
      break;
    case 'mute':
      toggleMusic();
      break;
    case 'noclip':
      toggleNoclip();
      break;
  }
};

menu.onChange = (what: SettingsChange) => {
  saveSettings(settings);
  switch (what) {
    case 'rules':
      if (touch) settings.autobhop = true;
      sim.cv.airaccelerate = settings.airaccel;
      sim.autobhop = settings.autobhop;
      sim.cv.slopeSlide = settings.slopeSlide;
      pb = getPB(settings, pbMap());
      restart();
      updateBoard();
      break;
    case 'fov':
      gr.setFov(settings.fov);
      break;
    case 'knife':
      gr.showKnife = settings.knife;
      break;
    case 'viewmodel':
      applyViewmodelSettings();
      break;
    case 'volume':
      sound.setVolume(settings.volume / 100);
      break;
    case 'music':
      music.setVolume(settings.musicVolume / 100);
      if (settings.musicVolume > 0 && settings.musicMuted) {
        settings.musicMuted = false;
        music.setMuted(false);
        saveSettings(settings);
      }
      if (musicWanted()) void music.setPlaying(true);
      break;
    case 'pixelRatio':
      gr.setMaxPixelRatio(settings.maxPixelRatio);
      break;
    case 'lowLatency':
      // A different kind of canvas: the page starts again with it.
      location.reload();
      break;
    case 'binds':
      input.setBinds(settings.binds);
      hud.setKeyLabels(settings.binds);
      editor.controlsKey = settings.binds.scores[0] ? inputName(settings.binds.scores[0]) : 'Tab';
      lockKeys();
      break;
    case 'editorBinds':
      editor.setBinds(settings.editorBinds);
      break;
    case 'fullscreen':
      if (!settings.fullscreen && document.fullscreenElement) document.exitFullscreen().catch(() => {});
      break;
  }
};

/* ---------------- Level builder ---------------- */

/** Level open in the builder, or being play-tested from it. */
let testing: LevelDef | null = null;
let saveTimer = 0;
const editor = new LevelEditor(gr.scene, {
  rebuild: (def) => {
    // Rebuild the level and its view, keeping the camera where it is.
    const p = sim.player;
    const pos = [...p.pos];
    const { yaw, pitch } = view;
    asBuilder(() => startBoxLevel(def, 'custom'));
    // Restarting the level snaps the view to the spawn: keep looking where you were.
    view.yaw = yaw;
    view.pitch = pitch;
    sim.resyncYaw(yaw);
    sim.setNoclip(true);
    sim.events = 0;
    sim.player.pos = pos as typeof p.pos;
    sim.player.prev = [...pos] as typeof p.pos;
    hud.hideFinish();
    clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => void saveEditing(false), 1000);
  },
  toast: (msg) => hud.toast(msg, 2500),
  playTest: (def) => playTest(def),
  catalog: (open) => {
    // The builder has a free cursor: the catalog just opens over it.
    if (!open) startPlaying();
  },
  openMaps: () => mapOpen.open(),
  music: () => ({
    on: !settings.musicMuted,
    title: music.current ? `${music.current.title} · ${music.current.artist}` : null,
  }),
  toggleMusic: () => toggleMusic(),
  project: (p) => gr.projectSource(p),
  anisotropy: gr.anisotropy,
});
/** The builder's side view: centre and width (updated in place every frame). */
const insetCenter = [0, 0, 0];
const insetView = { center: insetCenter, yaw: 0, width: 1400 };
/** Where the mouse is over the 3D view (-1..1 each way), for the builder's cursor. */
const cursorNdc = [0, 0];
const rayO = [0, 0, 0];
const rayD = [1, 0, 0];
lock.onLook = (looking) => document.body.classList.toggle('looking', looking);
editor.setBinds(settings.editorBinds);
editor.controlsKey = settings.binds.scores[0] ? inputName(settings.binds.scores[0]) : 'Tab';
boardReady = true;
updateBoard();

async function saveEditing(announce: boolean) {
  const m = editor.model;
  if (!m) return;
  try {
    await saveLevel(m.def);
    m.dirty = false;
    if (announce) hud.toast('Saved in this browser', 1500);
  } catch {
    hud.toast("Couldn't save: this browser blocks storage", 3000);
  }
}

/** Build on a copy of someone's map: saved in Your levels as a new level, then opened. */
function buildOnCopy(src: LevelDef, from: string) {
  const copy = structuredClone(src);
  copy.name = 'lvl_' + Date.now().toString(36);
  copy.title = (src.title + ' remix').slice(0, 40);
  delete copy.author;
  if (editor.active) closeEditor();
  openEditor(copy);
  void saveLevel(copy).then(refreshLevels);
  startPlaying();
  hud.toast(`Building on ${from}: your copy is "${copy.title}" in Your levels`, 4000);
}

const mapOpen = new MapOpen({
  builtins: () => BUILTIN_MAPS,
  builtinBoxes: (name) => builtinLevel(name)?.boxes ?? [],
  search: (query, page) => online.maps('top', query, page),
  openBuiltin: (name) => {
    const def = builtinLevel(name);
    if (def) buildOnCopy(def, def.title);
  },
  openPlayer: async (m) => {
    const r = await online.map(m.code);
    buildOnCopy(unpackLevel(fromBase64Url(r.level), 'p_' + m.code), `${m.title} by ${m.author}`);
  },
  shown: (open) => {
    if (!open) startPlaying();
  },
});

function openEditor(def: LevelDef) {
  testing = null;
  // Building uses a free mouse cursor; the right button captures it to look around.
  lock.setCursorMode(true);
  editor.start(def);
  gr.showKnife = false;
  asBuilder(() => startBoxLevel(editor.model!.def, 'custom'));
  sim.setNoclip(true);
  ($('levelTitle') as HTMLInputElement).value = def.title;
  updateBuilderPanels();
  showPaused();
  updateBoard();
}

function closeEditor() {
  clearTimeout(saveTimer);
  void saveEditing(false);
  editor.stop();
  lock.setCursorMode(false);
  gr.showKnife = settings.knife;
  updateBuilderPanels();
  updateBoard();
}

/** A level as the server would see it, so a finished run can publish it; as-is if it can't be (no end yet). */
function playable(def: LevelDef): LevelDef {
  try {
    return canonicalLevel(def);
  } catch {
    return structuredClone(def);
  }
}

/** Where the builder camera was when a play test started (going back puts it there again). */
let testFrom: { pos: number[]; yaw: number; pitch: number } | null = null;

function playTest(def: LevelDef) {
  clearTimeout(saveTimer);
  void saveEditing(false);
  testFrom = editor.active ? { pos: [...sim.player.pos], yaw: view.yaw, pitch: view.pitch } : null;
  const test = playable(def);
  testing = test;
  editor.stop();
  // Playing captures the mouse again (this runs from a key press or click, so it may).
  const inGame = lock.locked;
  lock.setCursorMode(false);
  gr.showKnife = settings.knife;
  asBuilder(() => startBoxLevel(test, 'custom'));
  updateBuilderPanels();
  if (inGame) startPlaying();
  // Fullscreen Chrome/Edge: the game gets Esc itself, so it can go back without freeing the mouse.
  lockKeys();
  const back = settings.editorBinds.playTest[0];
  hud.toast(`Play test: Esc${back ? ' or ' + inputName(back) : ''} goes back to building`, 3000);
}

/** End a play test: straight back into the builder, where the camera was. */
function backToEditor() {
  if (!testing) return;
  const def = testing;
  const from = testFrom;
  openEditor(def);
  if (from) {
    const p = sim.player;
    p.pos = [from.pos[0], from.pos[1], from.pos[2]];
    p.prev = [from.pos[0], from.pos[1], from.pos[2]];
    view.yaw = from.yaw;
    view.pitch = from.pitch;
    sim.resyncYaw(view.yaw);
  }
  lockKeys();
}

function updateBuilderPanels() {
  // The game HUD (timer, speed, stats) means nothing while building.
  document.body.classList.toggle('building', editor.active);
  $('editorPanel').hidden = !editor.active;
  $('testPanel').hidden = !testing;
}

function showPaused() {
  const title = editor.active ? (editor.model?.def.title ?? 'Level') + ' (building)' : levelName;
  // Community maps show who made them (remembered when the map was opened on GameBanana).
  const credit = levelKind === 'map' ? creditFor(levelName, currentMap?.sourceName ?? '') : null;
  mainMenu.setPaused(
    levelKind === 'published' ? 'custom' : levelKind,
    levelKind === 'published'
      ? (currentDef?.title ?? levelName)
      : levelKind === 'custom' && !editor.active
        ? (testing?.title ?? levelName)
        : title,
    credit
      ? `Map by ${credit.authors.join(', ')} · from GameBanana`
      : levelKind === 'published' && currentDef
        ? `By ${currentDef.author ?? 'a player'} · code ${publishedCode} · hold Tab for the hiscores`
        : '',
  );
  updateBuilderPanels();
}

async function refreshLevels() {
  const list = $('levelList');
  list.textContent = '';
  let levels;
  try {
    levels = await listLevels();
  } catch {
    const li = document.createElement('li');
    li.textContent = "This browser can't keep levels (private window or storage blocked).";
    list.append(li);
    return;
  }
  for (const l of levels) {
    const li = document.createElement('li');
    const name = document.createElement('div');
    name.className = 'lvlName';
    name.textContent = l.title;
    const meta = document.createElement('span');
    meta.textContent = `${l.boxes} pieces · edited ${new Date(l.updatedAt).toLocaleDateString()}`;
    if (l.published) {
      const pub = document.createElement('b');
      pub.className = 'pub';
      pub.textContent = ` · published as ${l.published}`;
      meta.append(pub);
    }
    name.append(meta);
    const btn = (label: string, fn: () => void, cls = 'secondary') => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = cls;
      b.textContent = label;
      b.addEventListener('click', fn);
      return b;
    };
    // Less used actions sit in a small menu.
    const more = document.createElement('details');
    more.className = 'more';
    const sum = document.createElement('summary');
    sum.className = 'secondary';
    sum.textContent = '⋯';
    sum.title = 'More';
    const menuBox = document.createElement('div');
    menuBox.className = 'moreMenu';
    const close = (fn: () => void) => () => {
      more.open = false;
      fn();
    };
    menuBox.append(
      ...(l.published
        ? [
            btn(
              'Copy link to the map',
              close(() => copyText(mapLink(l.published!), 'Copied the link to ' + l.published)),
              'link',
            ),
          ]
        : []),
      btn(
        'Copy as text',
        close(() => void copyLevelText(l.name)),
        'link',
      ),
      btn(
        'Export file',
        close(() => void exportSaved(l.name)),
        'link',
      ),
      btn(
        'Delete',
        close(() => {
          if (!confirm(`Delete "${l.title}" from this browser?`)) return;
          void deleteLevel(l.name).then(refreshLevels);
        }),
        'link danger',
      ),
    );
    more.append(sum, menuBox);
    li.append(
      name,
      btn('Edit', () => void editSaved(l.name), 'primaryBtn'),
      btn('Play', () => void playSaved(l.name)),
      l.published
        ? btn('Copy code', () =>
            copyText(l.published!, `Copied the code ${l.published}. Anyone can open it under Player maps.`),
          )
        : btn('Publish', () => void publishSaved(l.name)),
      more,
    );
    list.append(li);
  }
}

function levelMsg(msg: string) {
  $('levelMsg').textContent = msg;
  if (mainMenu.screen === 'paused') $('mapNote').textContent = msg;
}

function copyText(text: string, done: string) {
  navigator.clipboard
    ?.writeText(text)
    .then(() => levelMsg(done))
    .catch(() => levelMsg("Couldn't copy: the browser blocked the clipboard."));
}

/** The whole level as one line of text, for pasting anywhere (the "Open" box imports it). */
async function copyLevelText(name: string) {
  const def = await loadLevel(name);
  if (!def) return;
  const text = levelToCode(def);
  copyText(
    text,
    `Copied "${def.title}" as text (${Math.ceil(text.length / 1000)}k characters). Paste it in "Open" to import.`,
  );
}

/** Publish an own level. It must have been finished in its current form (the run is checked). */
async function publishSaved(name: string) {
  if (!online.me) {
    await account.requireName(
      'Published maps show who made them. You can change your name later in Settings.',
    );
    if (!online.me) {
      levelMsg("Publishing needs the hiscore server, which can't be reached right now.");
      return;
    }
  }
  const rec = await loadLevelRecord(name).catch(() => null);
  if (!rec) return;
  let def: LevelDef;
  try {
    def = canonicalLevel(rec.def);
  } catch (e) {
    levelMsg((e as Error).message);
    return;
  }
  if (!rec.proof || rec.proof.key !== levelKey(def)) {
    levelMsg(
      `Finish "${def.title}" first: Play test it from the start to the end platform` +
        (rec.proof ? ' (it changed since you last finished it).' : '.'),
    );
    return;
  }
  if (!confirm(`Publish "${def.title}" for everyone to play, as made by ${online.me.name}?`)) return;
  levelMsg('Publishing…');
  try {
    const r = await online.publish(def.title, packLevel(def), rec.proof.replay);
    await setPublished(name, r.code);
    levelMsg(`Published! Anyone can play it with the code ${r.code}.`);
  } catch (e) {
    const code = e instanceof OnlineError ? e.data.code : undefined;
    if (typeof code === 'string') await setPublished(name, code);
    levelMsg((e as Error).message);
  }
  await refreshLevels();
}

/** Published maps already downloaded this session. */
const publishedCache = new Map<string, LevelDef>();

async function playPublished(code: string) {
  playerMaps.status('Loading ' + code + '…');
  try {
    let def = publishedCache.get(code);
    if (!def) {
      const r = await online.map(code);
      def = unpackLevel(fromBase64Url(r.level), 'p_' + code);
      publishedCache.set(code, def);
    }
    if (editor.active) closeEditor();
    testing = null;
    publishedCode = code;
    startBoxLevel(def, 'published');
    updateBoard();
    playerMaps.status('');
    // Loading took a moment, so the click can't lock the mouse: show the map's pause screen.
    showPaused();
  } catch (e) {
    playerMaps.status((e as Error).message);
  }
}
playerMaps.onPlay = (code) => void playPublished(code);
playerMaps.onImportText = async (text) => {
  try {
    const def = levelFromCode(text, 'lvl_' + Date.now().toString(36));
    await saveLevel(def);
    await refreshLevels();
    levelMsg(`Imported "${def.title}" into Your levels.`);
  } catch (e) {
    playerMaps.status((e as Error).message);
  }
};

async function editSaved(name: string) {
  const def = await loadLevel(name);
  if (def) openEditor(def);
}

async function playSaved(name: string) {
  const def = await loadLevel(name);
  if (!def) return;
  if (editor.active) closeEditor();
  testing = null;
  startBoxLevel(playable(def), 'custom');
  updateBuilderPanels();
  mainMenu.setPaused('custom', def.title);
  startPlaying();
}

function downloadLevel(def: LevelDef) {
  const blob = new Blob([JSON.stringify(def)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = (def.title || def.name).replace(/[^a-z0-9_-]+/gi, '_') + '.bhoplevel.json';
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

async function exportSaved(name: string) {
  const def = await loadLevel(name);
  if (def) downloadLevel(def);
}

$('levelNew').addEventListener('click', () => {
  const name = 'lvl_' + Date.now().toString(36);
  openEditor(newLevel(name, 'My level'));
  void saveLevel(editor.model!.def).then(refreshLevels);
  // Straight into building (this is a click, so it may), then name the map.
  startPlaying();
  askMapName();
});

/** The name dialog shown when a new level starts. */
function askMapName() {
  const dlg = $('nameDialog');
  const input = $('nameDialogInput') as HTMLInputElement;
  input.value = '';
  dlg.hidden = false;
  setTimeout(() => input.focus(), 0);
}
function closeNameDialog(save: boolean) {
  const dlg = $('nameDialog');
  if (dlg.hidden) return;
  dlg.hidden = true;
  const name = ($('nameDialogInput') as HTMLInputElement).value.trim();
  if (save && name && editor.model) {
    editor.model.rename(name);
    ($('levelTitle') as HTMLInputElement).value = editor.model.def.title;
    void saveEditing(false).then(refreshLevels);
    hud.toast(`Building "${editor.model.def.title}"`, 2000);
  }
}
$('nameDialogOk').addEventListener('click', () => closeNameDialog(true));
$('nameDialogSkip').addEventListener('click', () => closeNameDialog(false));
$('nameDialogInput').addEventListener('keydown', (e) => {
  const k = (e as KeyboardEvent).key;
  if (k === 'Enter') closeNameDialog(true);
  else if (k === 'Escape') closeNameDialog(false);
  e.stopPropagation();
});
// Clicks on the dialog don't reach the builder.
$('nameDialog').addEventListener('mousedown', (e) => e.stopPropagation());
$('levelImport').addEventListener('click', () => ($('levelFile') as HTMLInputElement).click());
$('levelFile').addEventListener('change', async () => {
  const input = $('levelFile') as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;
  try {
    const def = parseLevelFile(await file.text());
    // Imported levels get their own id so they don't overwrite one of yours.
    def.name = 'lvl_' + Date.now().toString(36);
    await saveLevel(def);
    await refreshLevels();
  } catch (e) {
    alert((e as Error).message);
  }
});
$('levelSave').addEventListener('click', () => {
  editor.model?.rename(($('levelTitle') as HTMLInputElement).value);
  void saveEditing(true).then(refreshLevels);
});
$('levelTitle').addEventListener('change', () =>
  editor.model?.rename(($('levelTitle') as HTMLInputElement).value),
);
$('levelPlay').addEventListener('click', () => {
  if (!editor.model) return;
  editor.model.rename(($('levelTitle') as HTMLInputElement).value);
  playTest(editor.model.def);
  showPaused();
  startPlaying();
});
$('levelPublish').addEventListener('click', async () => {
  if (!editor.model) return;
  editor.model.rename(($('levelTitle') as HTMLInputElement).value);
  await saveEditing(false);
  await publishSaved(editor.model.def.name);
});
$('testPublish').addEventListener('click', () => {
  if (testing) void publishSaved(testing.name);
});
$('levelExport').addEventListener('click', () => {
  if (editor.model) downloadLevel(editor.model.def);
});
$('levelBack').addEventListener('click', () => {
  if (!testing) return;
  backToEditor();
  startPlaying();
});
void refreshLevels();

/* ---------------- Input events ---------------- */

/** Typing into a field (the builder's panel, the menu): keys are text, not game controls. */
const typing = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA');

window.addEventListener('keydown', (e) => {
  if (typing(e.target)) return;
  if (!lock.locked) {
    // Only the debug overlay works from the menu.
    if (input.actionFor(e.code) === 'debug' && !e.repeat) {
      e.preventDefault();
      toggleDebug();
    }
    return;
  }
  // Play test: Esc (when the browser hands it to the game) or the play test key goes back.
  if (
    testing &&
    !editor.active &&
    !e.repeat &&
    (e.code === 'Escape' ||
      (settings.editorBinds.playTest.includes(e.code) &&
        // P also changes skin in game; in a play test it goes back to building.
        (!input.actionFor(e.code) || input.actionFor(e.code) === 'randomFinish')))
  ) {
    e.preventDefault();
    backToEditor();
    return;
  }
  // Level builder keys (Esc first clears the selection).
  if (editor.active && editor.handleKey(e)) {
    e.preventDefault();
    return;
  }
  // Building with a free cursor: Esc opens the menu (no pointer lock to let go of).
  if (editor.active && lock.cursor && e.code === 'Escape' && !editor.catalogOpen && !mapOpen.isOpen) {
    e.preventDefault();
    lock.release();
    return;
  }
  // A number menu (!stage, !skins, ...) takes 0-9 while it's open.
  const menuDigit = /^(?:Digit|Numpad)(\d)$/.exec(e.code);
  if (menuDigit && radio.isOpen) {
    e.preventDefault();
    if (!e.repeat) radio.digit(Number(menuDigit[1]));
    return;
  }
  // Zone editor: pick the type of the zone just drawn.
  const digit = /^(?:Digit|Numpad)([123])$/.exec(e.code);
  if (zonePending && digit) {
    e.preventDefault();
    assignZone(Number(digit[1]) as 1 | 2 | 3);
    return;
  }
  const bound = input.press(e.code, e.repeat);
  if (bound || e.ctrlKey) e.preventDefault();
});
window.addEventListener('keyup', (e) => {
  input.release(e.code);
  if (editor.active) editor.handleKeyUp(e);
});
// Esc closes the builder's catalog or open-a-map window (the mouse is already free then).
window.addEventListener('keydown', (e) => {
  if (e.code !== 'Escape' || !(editor.catalogOpen || mapOpen.isOpen)) return;
  editor.closeCatalog(false);
  mapOpen.close(false);
  if (lock.cursor) return;
  menu.setVisible(true);
  showPaused();
});
window.addEventListener(
  'wheel',
  (e) => {
    if (!lock.locked) return;
    // Builder: only the 3D view zooms (the panel and catalog scroll).
    if (lock.cursor && e.target !== gr.canvas) return;
    e.preventDefault();
    // Chat open: the wheel scrolls its lines instead of jumping.
    if (chat.isOpen) {
      chat.scroll(e.deltaMode === 1 ? e.deltaY * 20 : e.deltaY);
      return;
    }
    if (editor.active) {
      // The builder zooms: the camera moves along the view instead of flying up.
      const d = editor.zoomStep(e.deltaY < 0, e.shiftKey);
      const cp = Math.cos(view.pitch);
      const p = sim.player.pos;
      p[0] += Math.cos(view.yaw) * cp * d;
      p[1] += Math.sin(view.yaw) * cp * d;
      p[2] -= Math.sin(view.pitch) * d;
      return;
    }
    input.wheel(e.deltaY < 0 ? 'WheelUp' : 'WheelDown');
  },
  { passive: false },
);
document.addEventListener('mousemove', (e) => {
  // Spectating: the view is the record holder's.
  if (!lock.locked || spectating) return;
  if (lock.cursor) {
    // Builder: the cursor aims (only over the 3D view, not the panel); the view turns while looking.
    if (!lock.looking) {
      editor.setHover(e.target === gr.canvas);
      const c = gr.canvas.getBoundingClientRect();
      cursorNdc[0] = ((e.clientX - c.left) / c.width) * 2 - 1;
      cursorNdc[1] = -(((e.clientY - c.top) / c.height) * 2 - 1);
      return;
    }
  }
  const k = settings.sens * 0.022 * DEG;
  view.yaw -= e.movementX * k;
  view.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, view.pitch + e.movementY * k));
  if (e.movementX) {
    turnDir = -Math.sign(e.movementX);
    turnDecay = 6;
  }
  gr.viewmodel.sway(e.movementX, e.movementY);
});
document.addEventListener('mousedown', (e) => {
  if (!lock.locked) return;
  if (editor.active) {
    // Free cursor: only clicks in the 3D view build (the panel and catalog have their own).
    if (lock.cursor && e.target !== gr.canvas) return;
    e.preventDefault();
    // Middle button held: look around. Right button: the piece menu.
    editor.closeMenu();
    if (lock.cursor && e.button === 1) {
      lock.startLook();
      return;
    }
    if (lock.cursor && e.button === 2) {
      editor.contextMenu(e.clientX, e.clientY);
      return;
    }
    if (editor.handleMouse(e.button, { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey })) return;
  }
  input.press('Mouse' + e.button);
});
document.addEventListener('mouseup', (e) => {
  input.release('Mouse' + e.button);
  if (lock.cursor && e.button === 1) lock.stopLook();
  if (editor.active) editor.handleMouseUp(e.button);
});
document.addEventListener('contextmenu', (e) => {
  if (lock.locked || editor.active) e.preventDefault();
});

lock.onMessage = (msg) => {
  $('lockMsg').textContent = msg;
};
lock.onChange = (locked, refused) => {
  // The builder's catalog releases the mouse without bringing up the menu.
  if (!locked && (editor.catalogOpen || mapOpen.isOpen)) {
    input.clear();
    return;
  }
  menu.setVisible(!locked);
  // While playing, a small reminder that Esc opens the menu (touch screens have a pause button).
  $('escHint').hidden = !locked || touch;
  updateMusicHint();
  if (touch) touchUi.setActive(locked);
  if (touch && locked && !touchHelpShown) {
    touchHelpShown = true;
    hud.toast(
      settings.touchAssist
        ? 'Push forward to run: hopping starts by itself at full speed. Swipe left and right on the right half to turn and gain speed.'
        : 'Push forward to run: hopping starts by itself at full speed.',
      5000,
    );
  }
  if (!locked) {
    editor.closeCatalog(false);
    mapOpen.close(false);
    input.clear();
    // Esc during a play test: back in the builder at once (a click carries on building).
    if (testing && !editor.active && !refused) backToEditor();
    if (mainMenu.screen !== 'loading') showPaused();
  } else sim.resyncYaw(view.yaw);
};
$('play').addEventListener('click', startPlaying);
gr.canvas.addEventListener('click', () => {
  if (!lock.locked && mainMenu.screen === 'paused') startPlaying();
});

/* ---------------- Keeping Ctrl+W from closing the tab ---------------- */

// Keys whose Ctrl combos close or replace the tab (W, T, N, Q, Tab...) plus every bound key. Escape is
// left out on purpose, so Esc still releases the mouse and leaves fullscreen.
const GUARDED_KEYS = [
  'KeyW',
  'KeyT',
  'KeyN',
  'KeyQ',
  'KeyR',
  'KeyD',
  'KeyS',
  'KeyA',
  'KeyF',
  'KeyC',
  'KeyE',
  'Tab',
];
type KeyboardLock = { lock?: (codes?: string[]) => Promise<void>; unlock?: () => void };
const keyboardApi = (navigator as Navigator & { keyboard?: KeyboardLock }).keyboard;

function lockKeys() {
  if (!keyboardApi?.lock || !document.fullscreenElement) return;
  const bound = Object.values(settings.binds)
    .flat()
    .filter((c) => !c.startsWith('Mouse') && !c.startsWith('Wheel') && c !== 'Escape');
  // During a play test Esc comes to the game too (it goes back to the builder); holding Esc still
  // leaves fullscreen.
  const esc = testing && !editor.active ? ['Escape'] : [];
  keyboardApi.lock([...new Set([...GUARDED_KEYS, ...bound, ...esc])]).catch(() => {});
}

/** Must run inside a click handler: fullscreen and pointer lock both need the click. */
function startPlaying() {
  // New players pick a name before their first run (not for building or play testing).
  if (account.needsName && !editor.active && !testing) {
    void account
      .requireName(
        'Your times go on the online hiscores under this name. You can change it later in Settings.',
      )
      .then(startPlaying);
    return;
  }
  if (settings.fullscreen && !document.fullscreenElement && document.documentElement.requestFullscreen) {
    document.documentElement
      .requestFullscreen({ navigationUI: 'hide' })
      .then(lockKeys)
      .catch((e: Error) => {
        // Say why: browsers refuse quietly otherwise.
        console.warn('Fullscreen refused:', e);
        hud.toast(`Fullscreen refused by the browser: ${e.message || e.name}`, 5000);
      });
  } else lockKeys();
  // Phones: keep the screen sideways while playing (Android; iPhones ignore this).
  if (touch)
    (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> })
      .lock?.('landscape')
      .catch(() => {});
  lock.request();
}

// Browsers without keyboard lock (or not fullscreen): ask before closing the tab mid-run.
window.addEventListener('beforeunload', (e) => {
  if (lock.locked) e.preventDefault();
});

if (window.matchMedia?.('(pointer: coarse)').matches && !window.matchMedia('(pointer: fine)').matches) {
  $('touchNote').hidden = false;
}

/* ---------------- Weapons ---------------- */

const ammoEl = $('ammo');
const ammoClip = $('ammoClip');
let shownAmmo = -1;
/** The weapon the viewmodel shows (a new map or a replay can change it without a switch event). */
let shownWeapon: WeaponId | null = 'knife';
function updateAmmo() {
  const w = sim.weapons;
  // Both dropped: no arms.
  gr.emptyHands = w.active === null;
  if (w.active && w.active !== shownWeapon) {
    gr.viewmodel.setWeapon(w.active, 0);
    gr.viewmodel.setSilenced(w.silenced);
  }
  shownWeapon = w.active;
  const show = w.active === 'usp';
  if (ammoEl.hidden === show) ammoEl.hidden = !show;
  const label = w.reloading ? -2 : w.ammo;
  if (label !== shownAmmo) {
    shownAmmo = label;
    ammoClip.textContent = w.reloading ? '…' : String(w.ammo);
    ammoEl.classList.toggle('low', !w.reloading && w.ammo <= 3);
  }
}

function handleWeaponEvents() {
  const w = sim.weapons;
  const ev = w.events;
  if (ev & WEV_SWITCHED && w.active) {
    gr.viewmodel.setWeapon(w.active, (w.active === 'usp' ? USP.deploy : KNIFE_DEPLOY) * TICK_DT);
    gr.viewmodel.setSilenced(w.silenced);
    sound.deploy(w.active === 'usp');
    shownWeapon = w.active;
  }
  if (ev & WEV_DROPPED && !w.active && !spectating)
    chat.add('Hands empty. Type !knife or !usp to get a weapon back.');
  if (ev & (WEV_SLASH | WEV_STAB)) {
    const stab = (ev & WEV_STAB) !== 0;
    gr.viewmodel.attack(stab ? 1 : 0);
    sound.swing(stab);
  }
  if (ev & WEV_FIRED) {
    gr.viewmodel.fire(w.silenced, w.ammo === 0);
    sound.shot(w.silenced);
    if (w.shot.hit) {
      gr.decals.add(w.shot.pos, w.shot.normal);
      // Glass that broke has its own sound (EV_BROKE).
      if (!(sim.events & EV_BROKE))
        sound.impact(
          sim.level.runtime?.surface?.(w.shot.solid, w.shot.pos) ?? 'concrete',
          soundPos(w.shot.pos),
        );
    }
  }
  if (ev & WEV_RELOAD) {
    gr.viewmodel.reload(USP.reload * TICK_DT);
    sound.reload(USP.reload * TICK_DT);
  }
  if (ev & WEV_SILENCER) {
    gr.viewmodel.silencer(USP.silencer * TICK_DT, !w.silenced);
    sound.silencer(!w.silenced, USP.silencer * TICK_DT);
  }
}

/** Distance and stereo side of a point, from the player's eye and view direction. */
const soundPosOut = { dist: 0, pan: 0 };
function soundPos(p: Readonly<[number, number, number]>) {
  const pl = sim.player;
  const dx = p[0] - pl.pos[0];
  const dy = p[1] - pl.pos[1];
  const dz = p[2] - pl.pos[2] - 64;
  soundPosOut.dist = Math.hypot(dx, dy, dz);
  // Source: +Y is left, so a positive angle from the view is to the left (negative pan).
  soundPosOut.pan = Math.hypot(dx, dy) > 1 ? -Math.sin(Math.atan2(dy, dx) - view.yaw) : 0;
  return soundPosOut;
}

/* ---------------- Main loop: fixed 100 tick, interpolated render ---------------- */

/** Footsteps while running on the ground (walking under 150 u/s and crouching are silent, like CS:S). */
let stepDistance = 0;
const eyeScratch = [0, 0, 0];
function footsteps() {
  const p = sim.player;
  const hs = horizontalSpeed(p);
  if (!p.ground || p.ducked || hs < 150) {
    stepDistance = 0;
    return;
  }
  stepDistance += hs * DT;
  if (stepDistance >= 76) {
    stepDistance = 0;
    sound.step(0.6);
  }
}

let last = performance.now();
let acc = 0;
/** The view angles the last tick used (catch-up ticks turn from these to the current view). */
let tickYaw = 0;
let tickPitch = 0;
function frame(now: number) {
  const cpuStart = performance.now();
  const frameMs = now - last;
  let dt = frameMs / 1000;
  last = now;
  if (dt > 0.25) dt = 0.25;
  if (touch && lock.locked && portrait.matches) checkOrientation();
  if (lock.locked) {
    if (touch) {
      const yaw0 = view.yaw;
      const turn = touchUi.look(view, dt, MAX_PITCH);
      if (turn) {
        turnDir = -Math.sign(turn);
        turnDecay = 6;
      }
      touchUi.strafeAssist(view.yaw - yaw0, sim.player.ground === null, horizontalSpeed(sim.player));
    }
    acc += dt;
    let n = 0;
    const t0 = performance.now();
    // A late frame runs several ticks at once: spread the mouse turn since the last tick over them, as
    // it came in, instead of all of it on the first (a stall would make your strafe one sharp turn).
    const ticks = Math.floor(acc / DT);
    let toYaw = view.yaw;
    let toPitch = view.pitch;
    while (acc >= DT) {
      if (spectating) spectateCmd(cmd);
      else {
        const f = ticks > 1 ? (n + 1) / ticks : 1;
        input.buildCmd(
          cmd,
          tickYaw + (toYaw - tickYaw) * f,
          tickPitch + (toPitch - tickPitch) * f,
          sim.cv.forwardspeed,
        );
        if (sim.noclip) recorder.invalidate();
        recorder.record(cmd);
      }
      sim.tick(cmd);
      if (sim.events) handleSimEvents();
      if (sim.weapons.events) handleWeaponEvents();
      footsteps();
      acc -= DT;
      n++;
      // The game turned the view itself (a teleport): carry on from there.
      if (view.yaw !== toYaw || view.pitch !== toPitch) {
        tickYaw = toYaw = view.yaw;
        tickPitch = toPitch = view.pitch;
      }
    }
    tickYaw = view.yaw;
    tickPitch = view.pitch;
    debug.recordTicks(n, performance.now() - t0);
    if (spectating) {
      // Their view, smoothed between ticks (the shortest way round for yaw).
      const s = spectating;
      const a = acc / DT;
      let dy = s.yaw1 - s.yaw0;
      dy -= Math.round(dy / (2 * Math.PI)) * 2 * Math.PI;
      view.yaw = s.yaw0 + dy * a;
      view.pitch = s.pitch0 + (s.pitch1 - s.pitch0) * a;
    }
  } else acc = 0;
  if (turnDecay > 0) turnDecay--;
  if (mapView)
    mapView.zones.setPreview(
      zonePending ?? (zoneCorner ? zoneFromCorners(zoneCorner, sim.player.pos) : null),
    );
  const msgs = sim.level.runtime?.messages;
  if (msgs?.length) {
    hud.toast(msgs.join('  ·  '));
    msgs.length = 0;
  }
  if (mapAudio) {
    const p = sim.player;
    eyeScratch[0] = p.pos[0];
    eyeScratch[1] = p.pos[1];
    eyeScratch[2] = p.pos[2] + 64;
    mapAudio.update(eyeScratch, view.yaw, view.pitch);
  }
  if (editor.active) {
    // Aim along the cursor (the view centre while looking around).
    if (lock.looking || !lock.cursor) gr.sourceRay(0, 0, rayO, rayD);
    else gr.sourceRay(cursorNdc[0], cursorNdc[1], rayO, rayD);
    editor.update(rayO, rayD, view.yaw);
    // Side view of what's being placed or moved: its height against the pieces around it.
    const fb = editor.sideView ? editor.focusBox() : null;
    if (fb) {
      insetCenter[0] = (fb.min[0] + fb.max[0]) / 2;
      insetCenter[1] = (fb.min[1] + fb.max[1]) / 2;
      insetCenter[2] = (fb.min[2] + fb.max[2]) / 2;
      insetView.yaw = view.yaw;
      // Wide enough for the piece and its neighbours, close enough to read thin blocks.
      insetView.width = Math.max(
        640,
        Math.min(3200, Math.max(fb.max[0] - fb.min[0], fb.max[1] - fb.min[1]) * 5),
      );
      gr.inset = insetView;
    } else gr.inset = null;
    $('sideViewTag').hidden = !fb;
  } else if (gr.inset) {
    gr.inset = null;
    $('sideViewTag').hidden = true;
  }
  updateAmmo();
  // Tab: the hiscores, or every control while building.
  const holdInfo = lock.locked && input.held('scores');
  scoreboard.setVisible(holdInfo && !editor.active);
  editor.showControls(holdInfo && editor.active);
  if (spectating) bot.hide();
  else bot.update(dt, gr.cameraPosition);
  const renderStart = performance.now();
  if (!spikeTest || SPIKE_PARTS[spikeTest.part] !== 'no drawing')
    gr.render(sim, view, lock.locked ? acc / DT : 1, dt);
  debug.recordRender(performance.now() - renderStart);
  hud.update(sim, input, pb, settings.airaccel, settings.autobhop, turnDecay > 0 ? turnDir : 0);
  debug.frame(now, frameMs, performance.now() - cpuStart, sim, lock.raw);
  spikeTestFrame(now, frameMs);
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

/* ---------------- Links to published maps (?map=CODE) ---------------- */

{
  const params = new URLSearchParams(location.search);
  const code = normalizeShareCode(params.get('map') ?? '');
  if (code) {
    params.delete('map');
    const rest = params.toString();
    history.replaceState(null, '', location.pathname + (rest ? '?' + rest : '') + location.hash);
    tabs.show('maps');
    void playPublished(code);
  }
}

if (import.meta.env.DEV) {
  // Dev-only handle for poking at the game from the console (not in production builds).
  Object.assign(window, {
    __bhop: {
      get sim() {
        return sim;
      },
      view,
      input,
      lock,
      settings,
      startLoad,
      gr,
      editor,
      sound,
      music,
      /** Run one frame by hand (the loop pauses while the page is hidden). */
      frame,
      recorder,
    },
  });
}
